package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import com.advaita.cloud.drive.shared.MeAccessDto
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.store.MyAccessStore
import com.advaita.cloud.drive.ui.store.isMyAccessUrl
import com.advaita.cloud.drive.ui.store.orgAdminText
import com.advaita.cloud.drive.ui.store.roleHint
import com.advaita.cloud.drive.ui.store.roleLabel
import com.advaita.cloud.drive.ui.store.statusSentence
import com.advaita.cloud.drive.ui.store.teamChip
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private const val MENU_ID = "account-menu-list"
private const val DIALOG_TITLE_ID = "my-access-title"
private const val SOURCE_NOTE = "Some details are unavailable right now."

/**
 * CR-6.5 and CR-6.6: the Account menu. The header keeps its Sign out button, because the sign-out
 * specs click it by name. The menu holds "My access" and, for an org admin and a team lead,
 * "Organization console ↗". The menu is a button with `aria-haspopup`, a `role=menu` list, and
 * `role=menuitem` items. Escape closes it and returns focus to the button.
 *
 * `?dialog=my-access` opens the dialog on load: the console's old `/me` route redirects there.
 */
@Composable
fun AccountMenu(access: MyAccessStore) {
    LaunchedEffect(Unit) {
        // A 401 here means the page is on its way to sign-in. A throw out of a LaunchedEffect
        // would cancel the recomposer (see App), so it stops here and App's own load redirects.
        try {
            access.load()
        } catch (e: RedirectingException) {
            console.log("[drive] my access aborted, signing in: ${e.message}")
            return@LaunchedEffect
        }
        if (isMyAccessUrl(window.location.search)) access.openDialog()
    }
    Div(attrs = { classes("account-menu") }) {
        Button(attrs = {
            classes("btn")
            attr("id", "account-menu-button")
            attr("aria-haspopup", "menu")
            attr("aria-expanded", access.menuOpen.toString())
            attr("aria-controls", MENU_ID)
            onClick { access.toggleMenu() }
        }) { Text("Account") }
        if (access.menuOpen) {
            Div(attrs = {
                classes("account-menu-list")
                attr("id", MENU_ID)
                attr("role", "menu")
                attr("aria-labelledby", "account-menu-button")
                onKeyDown { e ->
                    if (e.key == "Escape") {
                        e.stopPropagation()
                        access.closeMenu()
                        document.getElementById("account-menu-button")?.let { (it as? HTMLElement)?.focus() }
                    }
                }
            }) {
                Button(attrs = {
                    classes("account-item")
                    attr("role", "menuitem")
                    ref { el -> el.focus(); onDispose { } }
                    onClick { access.openDialog() }
                }) { Text("My access") }
                access.consoleUrl?.let { url ->
                    A(href = url, attrs = {
                        classes("account-item")
                        attr("role", "menuitem")
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    }) { Text("Organization console ↗") }
                }
            }
        }
    }
    if (access.dialogOpen) MyAccessDialog(access)
}

/** The My access dialog (v59 `vAccess`): status, role with its hint, teams, and the org-admin flag. */
@Composable
private fun MyAccessDialog(access: MyAccessStore) {
    DisposableEffect(Unit) {
        val opener = document.activeElement as? HTMLElement
        onDispose { opener?.takeIf { document.body?.contains(it) == true }?.focus() }
    }
    Div(attrs = { classes("modal-backdrop") }) {
        Div(attrs = {
            classes("modal", "my-access-modal")
            attr("role", "dialog")
            attr("aria-modal", "true")
            attr("aria-labelledby", DIALOG_TITLE_ID)
            onKeyDown { e ->
                when (e.key) {
                    "Escape" -> { e.stopPropagation(); access.closeDialog() }
                    "Tab" -> (e.currentTarget as? HTMLElement)?.let { trapTab(it, e.shiftKey) { e.preventDefault() } }
                }
            }
        }) {
            H2(attrs = { classes("modal-title"); attr("id", DIALOG_TITLE_ID) }) { Text("My access") }
            Div(attrs = { classes("modal-message") }) { Text("The Advaita products you can use and what you can do in them.") }
            val info = access.info
            when {
                access.failure != null -> Div(attrs = { classes("share-error"); attr("role", "alert") }) { Text(access.failure!!) }
                info == null -> Div(attrs = { classes("share-loading") }) { Text("Loading…") }
                else -> MyAccessCard(info)
            }
            Div(attrs = { classes("modal-actions") }) {
                Button(attrs = {
                    classes("btn", "primary")
                    ref { el -> el.focus(); onDispose { } }
                    onClick { access.closeDialog() }
                }) { Text("Done") }
            }
        }
    }
}

@Composable
private fun MyAccessCard(info: MeAccessDto) {
    Div(attrs = { classes("access-card") }) {
        Div(attrs = { classes("access-card-title") }) {
            Span { Text("DataHub") }
            Span(attrs = { classes("access-status") }) { Text(statusSentence(info.status)) }
        }
        Div(attrs = { classes("access-row") }) {
            Span(attrs = { classes("access-key") }) { Text("Role") }
            Span {
                Text(roleLabel(info.baseRole))
                roleHint(info.baseRole)?.let { Span(attrs = { classes("sub") }) { Text(" — $it") } }
            }
        }
        Div(attrs = { classes("access-row") }) {
            Span(attrs = { classes("access-key") }) { Text("Teams") }
            if (info.teams.isEmpty()) Span { Text("None") }
            else Span(attrs = { classes("access-teams") }) {
                info.teams.forEach { t -> Span(attrs = { classes("access-chip"); if (t.lead) classes("lead") }) { Text(teamChip(t)) } }
            }
        }
        Div(attrs = { classes("access-row") }) {
            Span(attrs = { classes("access-key") }) { Text("Org admin") }
            Span { Text(orgAdminText(info.orgAdmin)) }
        }
        if (info.servicesStatus != "ok" || info.teamsStatus != "ok") {
            Div(attrs = { classes("capped-notice"); attr("role", "status") }) { Text(SOURCE_NOTE) }
        }
    }
}
